// New SpamOrdersPage.js - Improved UI with better data visibility
import React, { useState, useEffect } from "react";
import { fetchConfirmingOrders, updateOrderStatus, deleteOrder } from "../../../api/OrderAPIs";
import { checkReturnMatch } from "../../../api/ReturnsAPIs";
import { fetchDesignById } from "../../../api/DesignAPIs";
import { fetchTypeById } from "../../../api/TypeAPIs";
import { useAuth } from "../../../utils/auth";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../../api/URL_Header";

// SVG Icons
const Icons = {
  Copy: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  ),
  Download: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  ),
  Phone: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  ),
  Location: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  Package: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  Money: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Calendar: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Note: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Trash: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  ChevronDown: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  ),
  ChevronUp: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ),
};

const StatusBadge = ({ status }) => {
  const styles = {
    Spam: "bg-red-100 text-red-800 border-red-200",
    Confirming: "bg-yellow-100 text-yellow-800 border-yellow-200",
  };
  const labels = {
    Spam: "🚫 سبام",
    Confirming: "⏳ قيد التأكيد",
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.Confirming}`}>
      {labels[status] || status}
    </span>
  );
};

const SkeletonLoader = () => {
  return (
    <div className="animate-pulse space-y-4">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex justify-between items-start mb-4">
            <div className="h-6 bg-gray-200 rounded w-24"></div>
            <div className="h-5 bg-gray-200 rounded w-20"></div>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

const OrderCard = ({ order, authState, onUpdateStatus, onDeleteOrder, onCopyInfo, onCopyItems, onDownload }) => {
  const [expandedItems, setExpandedItems] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden">
      {/* Card Header */}
      <div className="bg-gradient-to-l from-gray-50 to-white px-5 py-4 border-b border-gray-100">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">رقم الطلب:</span>
            <span className="font-bold text-lg text-gray-800">#{order.orderNumber}</span>
            <StatusBadge status={order.status} />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Icons.Calendar />
            <span>{order.date}</span>
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-5 space-y-4">
        {/* Customer Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-700">
              <span className="font-medium w-24">العميل:</span>
              <span className="text-gray-800">{order.name}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Icons.Phone />
              <span className="font-medium w-24">الهاتف:</span>
              <span className="text-gray-800 dir-ltr">{order.phoneNumber} {order.phoneNumber2 !== "Unknown Phone" && `- ${order.phoneNumber2}`}</span>
            </div>
            <div className="flex items-start gap-2 text-gray-700">
              <Icons.Location />
              <span className="font-medium w-24">العنوان:</span>
              <span className="text-gray-800 flex-1">{order.address}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <span className="font-medium w-24">المحافظة:</span>
              <span className="text-gray-800">{order.state}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-700">
              <Icons.Money />
              <span className="font-medium w-24">الإجمالي:</span>
              <span className="text-lg font-bold text-emerald-600">{order.total} ج.م</span>
            </div>
            {order.profit > 0 && (
              <div className="flex items-center gap-2 text-gray-700">
                <span className="font-medium w-24">الربح:</span>
                <span className="text-emerald-600">{order.profit} ج.م</span>
              </div>
            )}
            {order.note && (
              <div className="flex items-start gap-2 text-gray-700">
                <Icons.Note />
                <span className="font-medium w-24">ملاحظة:</span>
                <span className="text-amber-600 flex-1">{order.note}</span>
              </div>
            )}
          </div>
        </div>

        {/* Items Section */}
        {order.items && order.items.length > 0 && (
          <div className="border-t border-gray-100 pt-4 mt-2">
            <button
              onClick={() => setExpandedItems(!expandedItems)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mb-3"
            >
              <Icons.Package />
              <span className="font-medium">المنتجات ({order.items.length})</span>
              {expandedItems ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
            </button>
            
            {expandedItems && (
              <div className="space-y-2 mt-3">
                {order.items.map((item, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="flex flex-wrap justify-between items-start gap-2">
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">{item.name}</div>
                        <div className="flex flex-wrap gap-3 text-sm text-gray-500 mt-1">
                          <span>النوع: {item.type}</span>
                          <span>الكمية: {item.quantity}</span>
                          <span>السعر: {item.price} ج.م</span>
                          {item.code && <span>كود: {item.code}</span>}
                        </div>
                      </div>
                      {item.driveLink && (
                        <button
                          onClick={() => onDownload(item.driveLink, `${item.code || item.name}.jpg`)}
                          className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors text-sm"
                        >
                          <Icons.Download />
                          <span>تحميل</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="border-t border-gray-100 pt-4 mt-2">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onUpdateStatus(order.orderNumber, "Old")}
              className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              تحديث إلى قديم
            </button>
            <button
              onClick={() => onUpdateStatus(order.orderNumber, "Changes")}
              className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm font-medium"
            >
              تحديث إلى تغييرات
            </button>
            {(authState.role === "Admin" || authState.role === "Moderator") && (
              <button
                onClick={() => onDeleteOrder(order.orderNumber)}
                className="flex items-center gap-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm"
              >
                <Icons.Trash />
                <span>حذف</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfirmingOrders = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      const response = await fetch(
        `${API_BASE_URL_V2}/api/files/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("تم التحميل بنجاح!", { position: "top-right", autoClose: 2000 });
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("⚠️ فشل التحميل. تحقق من السيرفر.", { position: "top-right", autoClose: 3000 });
    }
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        const enrichedItems = await Promise.all(
          order.orderDetails.map(async (detail) => {
            const design = await fetchDesignById(detail.designId);
            const type = await fetchTypeById(detail.typeId);
            if (!design || !type) return null;
            return {
              id: detail.designId,
              name: design.Name,
              quantity: detail.quantity,
              price: detail.price,
              type: type.name,
              driveLink: design.code.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
              code: design.code || "None",
            };
          })
        ).then((items) => items.filter((item) => item !== null));

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          status: order.status || "Confirming",
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchConfirmingOrders(0, 10);
      if (data.length === 0 || data.length < 10) setHasMore(false);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!", { position: "top-right", autoClose: 3000 });
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchConfirmingOrders(currentPage, 10);
      if (data.length === 0 || data.length < 10) setHasMore(false);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!", { position: "top-right", autoClose: 3000 });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    let reason = "no reason (normal order)";
    if (newStatus === "Hold") {
      reason = window.prompt("يرجى إدخال سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب لوضع الطلب على الانتظار.", { position: "top-right", autoClose: 3000 });
        return;
      }
    } else if (newStatus === "Changes") {
      reason = window.prompt("يرجى إدخال سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب التغيير.", { position: "top-right", autoClose: 3000 });
        return;
      }
    }

    const confirmUpdate = window.confirm(`هل أنت متأكد من تحديث حالة الطلب رقم ${orderId} إلى ${newStatus}؟`);
    if (!confirmUpdate) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      toast.success(`تم تحديث حالة الطلب رقم ${orderId} إلى ${newStatus} بنجاح!`, { position: "top-right", autoClose: 3000 });
    } catch (err) {
      console.error("Error updating order status:", err);
      toast.error("فشل في تحديث حالة الطلب.", { position: "top-right", autoClose: 3000 });
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`, { position: "top-right", autoClose: 3000 });
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب.", { position: "top-right", autoClose: 3000 });
    }
  };

  const handleCopyOrderInfo = (order) => {
    const phone = `${order.phoneNumber}${order.phoneNumber2 !== "Unknown Phone" ? `-${order.phoneNumber2}` : ''}`;
    const text = `${order.name}\t${phone}\t\t${order.address}\t${order.state}\t${order.total}\tCOD`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success("تم نسخ معلومات العميل!", { position: "top-right", autoClose: 2000 });
    }).catch(() => {
      toast.error("فشل في النسخ.", { position: "top-right", autoClose: 3000 });
    });
  };

  const handleCopyItems = (order) => {
    const lines = order.items.map((item, index) => {
      return `${index + 1}-${order.items.length}\t\tPhone Cover (H)\t${item.type}\t\t1\t${item.driveLink || "No Link"}`;
    }).join('\n');
    navigator.clipboard.writeText(lines).then(() => {
      toast.success("تم نسخ المنتجات!", { position: "top-right", autoClose: 2000 });
    }).catch(() => {
      toast.error("فشل في النسخ.", { position: "top-right", autoClose: 3000 });
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6" dir="rtl">
      <ToastContainer position="top-right" rtl={true} />
      
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">الطلبات المؤكدة</h1>
            <p className="text-gray-500 text-sm mt-1">الطلبات التي تم تأكيدها من قبل العملاء</p>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>رجوع</span>
          </button>
        </div>

        {/* Stats Summary */}
        {/* {loadedOrders.length > 0 && !isOrdersLoading && (
          <div className="bg-gradient-to-l from-blue-50 to-indigo-50 rounded-xl p-4 mb-6 border border-blue-100">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-sm text-gray-600">إجمالي الطلبات</span>
                <span className="text-2xl font-bold text-blue-600 block">{loadedOrders.length}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">إجمالي المبيعات</span>
                <span className="text-2xl font-bold text-emerald-600 block">
                  {loadedOrders.reduce((sum, o) => sum + (o.total || 0), 0).toLocaleString()} ج.م
                </span>
              </div>
            </div>
          </div>
        )} */}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6 flex items-center gap-3">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Orders List */}
        {isOrdersLoading ? (
          <SkeletonLoader />
        ) : (
          <>
            {loadedOrders.length > 0 ? (
              <div className="space-y-4">
                {loadedOrders.map((order) => (
                  <OrderCard
                    key={order.orderNumber}
                    order={order}
                    authState={authState}
                    onUpdateStatus={handleUpdateStatus}
                    onDeleteOrder={handleDeleteOrder}
                    onCopyInfo={handleCopyOrderInfo}
                    onCopyItems={handleCopyItems}
                    onDownload={handleDownload}
                  />
                ))}
                
                {/* Load More Button */}
                {hasMore && (
                  <div className="text-center pt-4">
                    <button
                      onClick={loadMoreOrders}
                      disabled={isLoadingMore}
                      className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 shadow-sm"
                    >
                      {isLoadingMore ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          جاري التحميل...
                        </span>
                      ) : (
                        "تحميل المزيد"
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-gray-500 text-lg">لا توجد طلبات مؤكدة</p>
                <p className="text-gray-400 text-sm mt-1">سيتم عرض الطلبات التي يؤكدها العملاء هنا</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmingOrders;